import { Immutable } from "immer";
import React from "react";
import { t } from "ttag";

import {
    UGCListSubBlock,
    UGCListSubBlockData,
    UGCPost,
} from "../../models/ugc";
import { fetchJson } from "../../utils/ajax";
import { fromSSRData } from "../../utils/ssr";
import { UGCGrid } from "../UGCGrid";

interface UGCApiResponse {
    meta: {
        total_count: number;
    };
    items: UGCPost[];
}

type IProps = Immutable<{
    pageSize: number;
    random?: boolean;
    // Accept UGC data from both template serialization (plain strings) and API (branded types)
    // UGCListSubBlockData: from Django template serialization with plain string URLs
    // UGCListSubBlock: from API responses with branded ImageURL types
    initialGridData?: readonly (UGCListSubBlock | UGCListSubBlockData)[];
    initialNextUrl?: string | null;
}>;

export const UGCApiGrid = ({
    pageSize,
    random = false,
    initialGridData,
    initialNextUrl,
}: IProps): React.ReactNode => {
    const [posts, setPosts] = React.useState<UGCListSubBlock[]>(() => {
        if (!initialGridData) return [];
        return fromSSRData<UGCListSubBlock[]>(initialGridData);
    });
    const [limit, setLimit] = React.useState<number | undefined>(undefined);
    const pageRef = React.useRef<number>(
        initialGridData ? Math.ceil(initialGridData.length / pageSize) : 0,
    );
    const [nextUrl, setNextUrl] = React.useState<string | null>(
        initialNextUrl ?? null,
    );
    const nextUrlRef = React.useRef<string | null>(initialNextUrl ?? null);
    const isInitialLoadRef = React.useRef(
        !!initialGridData && initialGridData.length > 0,
    );
    const [isLoadingMore, setIsLoadingMore] = React.useState(false);
    const isLoadingMoreRef = React.useRef(false);
    const [error, setError] = React.useState<string | null>(null);

    const abortControllerRef = React.useRef<AbortController | null>(null);

    // Abort any in-flight request on unmount
    React.useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
        };
    }, []);

    const getNextPage = React.useCallback(
        async (signal?: AbortSignal): Promise<void> => {
            if (isLoadingMoreRef.current) return;
            isLoadingMoreRef.current = true;
            setIsLoadingMore(true);
            setError(null);
            try {
                let url: string;
                if (nextUrlRef.current) {
                    url = nextUrlRef.current;
                } else {
                    url = `/api/cms/v2/social/?limit=${pageSize}`;
                    if (random) {
                        url += "&order=random";
                    } else {
                        url += `&offset=${pageSize * pageRef.current}`;
                    }
                }

                const response = await fetchJson<UGCApiResponse>(url, {
                    signal,
                });
                if (
                    !Array.isArray(response.items) ||
                    !response.meta ||
                    typeof response.meta.total_count !== "number"
                ) {
                    throw new Error("Invalid API response structure");
                }
                if (signal?.aborted) return;
                const items = response.items.map((post) => ({
                    post: post,
                }));
                const totalCount = response.meta.total_count;
                setPosts((prevPosts) => [...prevPosts, ...items]);
                pageRef.current = pageRef.current + 1;
                setLimit(totalCount);

                // Update nextUrl for pagination
                if (!random && totalCount > pageSize * pageRef.current) {
                    const newNextUrl = `/api/cms/v2/social/?limit=${pageSize}&offset=${pageSize * pageRef.current}`;
                    nextUrlRef.current = newNextUrl;
                    setNextUrl(newNextUrl);
                } else {
                    nextUrlRef.current = null;
                    setNextUrl(null);
                }
            } catch (err) {
                if (err instanceof DOMException && err.name === "AbortError") {
                    return;
                }
                console.error("Failed to load UGC data:", err);
                if (!signal?.aborted) {
                    setError(t`Failed to load posts. Please try again.`);
                }
            } finally {
                if (!signal?.aborted) {
                    isLoadingMoreRef.current = false;
                    setIsLoadingMore(false);
                }
            }
        },
        [pageSize, random],
    );

    React.useEffect(() => {
        // Skip initial load if we have SSR data -- nextUrl state (set from
        // initialNextUrl) already drives "Load More" button visibility.
        if (isInitialLoadRef.current) {
            isInitialLoadRef.current = false;
            return;
        }
        const controller = new AbortController();
        abortControllerRef.current = controller;
        getNextPage(controller.signal).catch((error) => {
            if (error instanceof DOMException && error.name === "AbortError") {
                return;
            }
            console.error("Failed to fetch initial UGC data:", error);
        });
        return () => {
            controller.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            {error && (
                <p className="posts__error" role="alert">
                    {error}
                </p>
            )}
            <UGCGrid tiles={posts} />
            {!random && (nextUrl || (limit && posts.length < limit)) ? (
                <button
                    className="button button--full-width button--alabaster load-more-posts"
                    disabled={isLoadingMore}
                    onClick={() => {
                        abortControllerRef.current?.abort();
                        const controller = new AbortController();
                        abortControllerRef.current = controller;
                        getNextPage(controller.signal).catch((error) => {
                            if (
                                error instanceof DOMException &&
                                error.name === "AbortError"
                            ) {
                                return;
                            }
                            console.error("Failed to load more posts:", error);
                        });
                    }}
                >
                    {isLoadingMore ? t`Loading...` : t`Load More Posts`}
                </button>
            ) : null}
        </>
    );
};
